import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const PaymentGate = ({ 
  children, 
  feature, 
  userSubscriptionStatus = 'free',
  showUpgradeModal = false,
  onUpgradeClick = null,
  customMessage = null 
}) => {
  const [showModal, setShowModal] = useState(showUpgradeModal);

  const featureMessages = {
    'search': {
      title: 'Upgrade to Find Travel Companions',
      message: 'Paid members can search and connect with compatible travelers. Get detailed personality matches and contact information.',
      benefits: [
        'View detailed personality match scores',
        'See full travel preferences and interests',
        'Direct messaging with potential companions',
        'Access to premium travel groups'
      ]
    },
    'messages': {
      title: 'Upgrade to Send Messages',
      message: 'Paid members can initiate conversations and respond to messages from other travelers.',
      benefits: [
        'Send unlimited messages to other members',
        'Respond to travel invitations',
        'Organize meetups and travel plans',
        'Build lasting travel friendships'
      ]
    },
               'local-meets': {
             title: 'Upgrade to Attend Local Meets',
             message: 'Paid members can see exact locations, times, and attendee details. Free users only see general location and meet type.',
             benefits: [
               'See exact location and full address',
               'Get specific start and end times',
               'View attendee list with personality scores',
               'Contact organizers and other attendees',
               'Join meets directly from the platform'
             ]
           },
    'organize-meets': {
      title: 'Upgrade to Organize Local Meets',
      message: 'Paid members can create and manage local meetups. Invite others and build your local travel community.',
      benefits: [
        'Create unlimited local meetups',
        'Invite both free and paid members',
        'Manage attendee lists and details',
        'Build your local travel network'
      ]
    },
    'travel-groups': {
      title: 'Upgrade to Join Travel Groups',
      message: 'Paid members can join organized travel groups and see detailed information about group members.',
      benefits: [
        'Join premium travel groups',
        'See group member profiles and scores',
        'Participate in group discussions',
        'Access exclusive travel opportunities'
      ]
    },
    'organize-groups': {
      title: 'Upgrade to Organize Travel Groups',
      message: 'Premium organizers can create and manage travel groups. Lead adventures and build your travel community.',
      benefits: [
        'Create and manage travel groups',
        'Invite members to your groups',
        'Organize group trips and events',
        'Build your travel leadership reputation'
      ]
    },
    'profile-features': {
      title: 'Upgrade Your Profile',
      message: 'Paid members get enhanced profile features that make them more attractive to potential travel companions.',
      benefits: [
        'Detailed personality match scores',
        'Enhanced profile visibility',
        'Priority in search results',
        'Access to premium features'
      ]
    },
    'mobile-app': {
      title: 'Upgrade to Access Mobile App',
      message: 'Paid members get exclusive access to our mobile app with AI-powered features and real-time notifications.',
      benefits: [
        'Real-time messaging with push notifications',
        'GPS-based local meet discovery',
        'AI-powered travel recommendations',
        'Safety alerts and location sharing',
        'Offline personality test completion',
        'Photo sharing and profile management'
      ]
    }
  };

  const getFeatureInfo = () => {
    return featureMessages[feature] || {
      title: 'Upgrade Required',
      message: 'This feature requires a paid membership.',
      benefits: [
        'Access to premium features',
        'Enhanced profile visibility',
        'Direct messaging capabilities',
        'Event organization tools'
      ]
    };
  };

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      window.location.href = '/pricing';
    }
  };

  // If user is paid, show the content
  if (userSubscriptionStatus === 'paid' || userSubscriptionStatus === 'premium') {
    return children;
  }

  // For free users, show upgrade modal or gate
  return (
    <>
      {children}
      
      {/* Upgrade Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">⭐</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {getFeatureInfo().title}
              </h2>
              <p className="text-gray-600">
                {customMessage || getFeatureInfo().message}
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Paid Member Benefits:</h3>
              <ul className="space-y-2">
                {getFeatureInfo().benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-success mr-2">✓</span>
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-r from-primary to-primary-dark rounded-lg p-4 mb-6">
              <h4 className="text-white font-semibold mb-2">Why Upgrade?</h4>
              <p className="text-white/90 text-sm">
                Join thousands of travelers who trust GradedWash for safe, compatible travel companions. 
                Our personality matching system ensures you connect with people who share your travel style and interests.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleUpgradeClick}
                className="btn btn-primary flex-1"
              >
                Upgrade Now
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary flex-1"
              >
                Maybe Later
              </button>
            </div>

            <div className="text-center mt-4">
              <p className="text-xs text-gray-500">
                Safe • Secure • Trusted by travelers worldwide
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentGate; 